import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Currency,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Select,
  Tag,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type PeriodType = 'month' | 'quarter' | 'fy' | 'custom'
type Scope = 'project' | 'all'
type Sort = 'latest' | 'highest'
type PaymentMethod = '' | 'cash' | 'account' | 'petty_cash' | 'third_party'

type PeriodState = {
  period_type: PeriodType
  period_anchor: string
  from: string
  to: string
}

type ExportResponse = { format: string; content: string; pdf_url?: string | null }

type AgencyRow = {
  agency_name: string
  labour_category_id: string | null
  labour_category_name: string | null
  payment_count: number
  paid_cash: number
  paid_account: number
  paid_petty_cash: number
  paid_third_party: number
  third_party_name: string | null
  total_paid: number
  last_payment_date: number
}

type Report = {
  period: { type: string; from: number; to: number; label: string }
  totals: {
    agencies_count: number
    payment_count: number
    paid_cash: number
    paid_account: number
    paid_petty_cash: number
    paid_third_party: number
    total_paid: number
  }
  by_project: {
    project_id: string
    project_name: string
    total_paid: number
    agencies_count: number
    payment_count: number
  }[]
  agencies: AgencyRow[]
  pagination: { total: number; limit: number; offset: number }
}

type Ledger = {
  agency_name: string
  totals: { total_paid: number; payment_count: number }
  entries: {
    id: string
    date: number
    amount: number
    payment_method: string
    third_party_name: string | null
    description: string | null
    project_id: string
    project_name: string
    running_total: number
  }[]
}

function buildPeriodQuery(p: PeriodState): URLSearchParams {
  const params = new URLSearchParams()
  params.set('period_type', p.period_type)
  if (p.period_type === 'custom') {
    if (p.from) params.set('from_date', String(Math.floor(new Date(`${p.from}T00:00:00Z`).getTime() / 1000)))
    if (p.to) params.set('to_date', String(Math.floor(new Date(`${p.to}T23:59:59Z`).getTime() / 1000)))
  } else if (p.period_anchor.trim()) {
    params.set('period_anchor', p.period_anchor.trim())
  }
  params.set('limit', '200')
  return params
}

function buildReportQuery(opts: {
  period: PeriodState
  scope: Scope
  projectId?: string
  sort: Sort
  search: string
  labourCategoryId: string
  paymentMethod: PaymentMethod
}): string {
  const params = buildPeriodQuery(opts.period)
  params.set('sort', opts.sort)
  if (opts.scope === 'project' && opts.projectId) {
    params.set('project_id', opts.projectId)
  }
  if (opts.search.trim()) params.set('search', opts.search.trim())
  if (opts.labourCategoryId) params.set('labour_category_id', opts.labourCategoryId)
  if (opts.paymentMethod) params.set('payment_method', opts.paymentMethod)
  return params.toString()
}

function openExportedPdf(res: ExportResponse) {
  if (res.pdf_url) window.open(res.pdf_url, '_blank', 'noopener,noreferrer')
}

function formatPaymentMethod(method: string): string {
  return method.replace(/_/g, ' ')
}

const REPORT_BASE = '/accounts/reports/labour/agencies'

export function LabourAgencyReportPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [scope, setScope] = useState<Scope>('project')
  const [period, setPeriod] = useState<PeriodState>({
    period_type: 'fy',
    period_anchor: '',
    from: '',
    to: '',
  })
  const [sort, setSort] = useState<Sort>('latest')
  const [search, setSearch] = useState('')
  const [labourCategoryId, setLabourCategoryId] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('')
  const [selectedAgency, setSelectedAgency] = useState<string | null>(null)

  const qs = buildReportQuery({
    period,
    scope,
    projectId,
    sort,
    search,
    labourCategoryId,
    paymentMethod,
  })

  const categories = useQuery({
    queryKey: ['labour-categories'],
    queryFn: () => fetchData<{ id: string; name: string }[]>(orgPath('/accounts/labour-categories')),
  })

  const report = useQuery<Report>({
    queryKey: ['labour-agency-report', projectId, scope, qs],
    enabled: scope === 'all' || Boolean(projectId),
    queryFn: () => fetchData<Report>(orgPath(`${REPORT_BASE}?${qs}`)),
  })

  const ledger = useQuery<Ledger>({
    queryKey: ['labour-agency-ledger', projectId, scope, selectedAgency, qs],
    enabled: Boolean(selectedAgency) && (scope === 'all' || Boolean(projectId)),
    queryFn: () =>
      fetchData<Ledger>(
        orgPath(
          `${REPORT_BASE}/${encodeURIComponent(selectedAgency!)}/ledger?${qs}`,
        ),
      ),
  })

  const exportSummary = useMutation({
    mutationFn: (detail: boolean) =>
      fetchData<ExportResponse>(
        orgPath(`${REPORT_BASE}/export?${qs}&format=pdf${detail ? '&detail=true' : ''}`),
      ),
    onError: flagIfAuthError,
    onSuccess: openExportedPdf,
  })

  const exportLedger = useMutation({
    mutationFn: (agencyName: string) =>
      fetchData<ExportResponse>(
        orgPath(
          `${REPORT_BASE}/${encodeURIComponent(agencyName)}/ledger/export?${qs}&format=pdf`,
        ),
      ),
    onError: flagIfAuthError,
    onSuccess: openExportedPdf,
  })

  const hideProjectColumn = scope === 'project'
  const resolved = report.data?.period

  return (
    <div className="space-y-5">
      <PageHeader
        title="Labour expense"
        subtitle="Agency-wise labour payments for the selected period — print or share this statement; not the live labour expense screens."
      />

      <div className="flex gap-2">
        {(
          [
            ['project', 'This project'],
            ['all', 'All Projects'],
          ] as const
        ).map(([s, label]) => (
          <Button
            key={s}
            variant={scope === s ? 'primary' : 'ghost'}
            onClick={() => {
              setScope(s)
              setSelectedAgency(null)
            }}
          >
            {label}
          </Button>
        ))}
      </div>

      <Card>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Period type">
            <Select
              value={period.period_type}
              onChange={(e) =>
                setPeriod((p) => ({ ...p, period_type: e.target.value as PeriodType }))
              }
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="fy">Financial year</option>
              <option value="custom">Custom range</option>
            </Select>
          </Field>
          {period.period_type === 'custom' ? (
            <>
              <Field label="From">
                <Input
                  type="date"
                  value={period.from}
                  onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                />
              </Field>
              <Field label="To">
                <Input
                  type="date"
                  value={period.to}
                  onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                />
              </Field>
            </>
          ) : (
            <Field
              label="Anchor (optional)"
              hint="e.g. 2026-04 (month), 2026-Q1 (quarter), 2026-27 (FY). Blank = current."
            >
              <Input
                value={period.period_anchor}
                onChange={(e) => setPeriod((p) => ({ ...p, period_anchor: e.target.value }))}
                placeholder="current"
              />
            </Field>
          )}
        </div>
      </Card>

      <Card>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Sort">
            <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="latest">Latest payment</option>
              <option value="highest">Highest total</option>
            </Select>
          </Field>
          <Field label="Search agency">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Agency name…"
            />
          </Field>
          <Field label="Trade / department">
            <Select
              value={labourCategoryId}
              onChange={(e) => setLabourCategoryId(e.target.value)}
            >
              <option value="">All trades</option>
              <option value="uncategorized">Uncategorized</option>
              {categories.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Payment method">
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            >
              <option value="">All methods</option>
              <option value="cash">Cash</option>
              <option value="account">Account</option>
              <option value="petty_cash">Petty cash</option>
              <option value="third_party">Third party</option>
            </Select>
          </Field>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag>Labour agencies</Tag>
          {resolved && <span className="text-sm text-surface-400">{resolved.label}</span>}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => exportSummary.mutate(false)}
            disabled={exportSummary.isPending}
          >
            {exportSummary.isPending ? 'Exporting…' : 'Export PDF'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => exportSummary.mutate(true)}
            disabled={exportSummary.isPending}
          >
            Export PDF with detail
          </Button>
        </div>
      </div>
      {exportSummary.isError && <ErrorBanner error={exportSummary.error} />}

      {report.isLoading && <Loading />}
      {report.isError && <ErrorBanner error={report.error} />}

      {report.data && (
        <ReportView
          report={report.data}
          scope={scope}
          selectedAgency={selectedAgency}
          onSelectAgency={setSelectedAgency}
          hideProjectColumn={hideProjectColumn}
          ledger={ledger}
          exportLedger={exportLedger}
        />
      )}
    </div>
  )
}

function ReportView({
  report,
  scope,
  selectedAgency,
  onSelectAgency,
  hideProjectColumn,
  ledger,
  exportLedger,
}: {
  report: Report
  scope: Scope
  selectedAgency: string | null
  onSelectAgency: (name: string | null) => void
  hideProjectColumn: boolean
  ledger: {
    isLoading: boolean
    isError: boolean
    error: unknown
    data?: Ledger
  }
  exportLedger: {
    mutate: (agencyName: string) => void
    isPending: boolean
    isError: boolean
    error: unknown
  }
}) {
  const { totals } = report

  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 text-sm">
          <Totals label="Total paid" value={totals.total_paid} />
          <Totals label="Cash" value={totals.paid_cash} />
          <Totals label="Account" value={totals.paid_account} />
          <Totals label="Petty cash" value={totals.paid_petty_cash} />
          <Totals label="Third party" value={totals.paid_third_party} />
          <Totals label="Agencies" count={totals.agencies_count} />
          <Totals label="Payments" count={totals.payment_count} />
        </div>
      </Card>

      {report.agencies.length === 0 ? (
        <Empty message="No labour payments in this period." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Agency</th>
                  <th className="py-2 pr-4">Trade</th>
                  <th className="py-2 pr-4 text-right">Payments</th>
                  <th className="py-2 pr-4 text-right">Cash</th>
                  <th className="py-2 pr-4 text-right">Account</th>
                  <th className="py-2 pr-4 text-right">Petty cash</th>
                  <th className="py-2 pr-4 text-right">Third party</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                  <th className="py-2 text-right">Last paid</th>
                </tr>
              </thead>
              <tbody>
                {report.agencies.map((a) => (
                  <tr
                    key={a.agency_name}
                    className={`border-b border-surface-700/50 cursor-pointer hover:bg-surface-800/50 ${
                      selectedAgency === a.agency_name ? 'bg-surface-800/70' : ''
                    }`}
                    onClick={() =>
                      onSelectAgency(selectedAgency === a.agency_name ? null : a.agency_name)
                    }
                  >
                    <td className="py-2 pr-4 font-medium">{a.agency_name}</td>
                    <td className="py-2 pr-4 text-surface-300">
                      {a.labour_category_name ?? 'Uncategorized'}
                    </td>
                    <td className="py-2 pr-4 text-right">{a.payment_count}</td>
                    <td className="py-2 pr-4 text-right">
                      <Currency amount={a.paid_cash} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Currency amount={a.paid_account} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Currency amount={a.paid_petty_cash} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <div>
                        <Currency amount={a.paid_third_party} />
                      </div>
                      {a.third_party_name && (
                        <div className="text-xs text-surface-400">{a.third_party_name}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right font-medium">
                      <Currency amount={a.total_paid} />
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <FormattedDate ts={a.last_payment_date} />
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-surface-600 font-semibold">
                  <td className="py-2 pr-4">Total</td>
                  <td className="py-2 pr-4">—</td>
                  <td className="py-2 pr-4 text-right">{totals.payment_count}</td>
                  <td className="py-2 pr-4 text-right">
                    <Currency amount={totals.paid_cash} />
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Currency amount={totals.paid_account} />
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Currency amount={totals.paid_petty_cash} />
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Currency amount={totals.paid_third_party} />
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Currency amount={totals.total_paid} />
                  </td>
                  <td className="py-2 text-right">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {scope === 'all' && report.by_project.length > 1 && (
        <Card>
          <h3 className="text-sm font-semibold mb-3">By project</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Project</th>
                  <th className="py-2 pr-4 text-right">Labour total</th>
                  <th className="py-2 pr-4 text-right">Agencies</th>
                  <th className="py-2 text-right">Payments</th>
                </tr>
              </thead>
              <tbody>
                {report.by_project.map((p) => (
                  <tr key={p.project_id} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4">{p.project_name}</td>
                    <td className="py-2 pr-4 text-right">
                      <Currency amount={p.total_paid} />
                    </td>
                    <td className="py-2 pr-4 text-right">{p.agencies_count}</td>
                    <td className="py-2 text-right">{p.payment_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selectedAgency && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Ledger — {selectedAgency}</h3>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => exportLedger.mutate(selectedAgency)}
                disabled={exportLedger.isPending}
              >
                {exportLedger.isPending ? 'Exporting…' : 'Export this agency PDF'}
              </Button>
              <Button variant="ghost" onClick={() => onSelectAgency(null)}>
                Close
              </Button>
            </div>
          </div>
          {exportLedger.isError && <ErrorBanner error={exportLedger.error} />}
          {ledger.isLoading && <Loading />}
          {ledger.isError && <ErrorBanner error={ledger.error} />}
          {ledger.data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-surface-400 border-b border-surface-700">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Method</th>
                    {!hideProjectColumn && <th className="py-2 pr-4">Project</th>}
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 text-right">Running total</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.data.entries.map((e) => (
                    <tr key={e.id} className="border-b border-surface-700/50">
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <FormattedDate ts={e.date} />
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <Currency amount={e.amount} />
                      </td>
                      <td className="py-2 pr-4">
                        <div>{formatPaymentMethod(e.payment_method)}</div>
                        {e.payment_method === 'third_party' && e.third_party_name && (
                          <div className="text-xs text-surface-400">{e.third_party_name}</div>
                        )}
                      </td>
                      {!hideProjectColumn && (
                        <td className="py-2 pr-4 text-surface-300">{e.project_name}</td>
                      )}
                      <td className="py-2 pr-4 text-surface-300">{e.description ?? '—'}</td>
                      <td className="py-2 text-right font-medium">
                        <Currency amount={e.running_total} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function Totals({ label, value, count }: { label: string; value?: number; count?: number }) {
  return (
    <div>
      <div className="text-xs text-surface-400">{label}</div>
      <div className="text-lg font-semibold mt-0.5">
        {count !== undefined ? count : <Currency amount={value ?? 0} />}
      </div>
    </div>
  )
}
